import { useEffect, useState } from "react";
import { isDesktopSearchPaletteOpen } from "../../../components/search/DesktopSearchPaletteHost";

/**
 * Which figure of the article is open, and how. A figure opens in the
 * `panel`, docked right of the text, which keeps scrolling (Figma
 * "D3.explore.article.figure-open"). From there `viewer` shows it full screen
 * ("D3.explore.article.figure-viewer"), and leaving full screen returns to
 * the panel.
 */
export type FigureView =
  | { kind: "closed" }
  | { kind: "panel"; index: number }
  | { kind: "viewer"; index: number };

// Whether the focus is in a field that takes the arrow keys itself.
export function isTyping(): boolean {
  const focused = document.activeElement;
  if (focused === null) return false;
  return (
    focused.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable=""]'
    ) !== null
  );
}

function clamp(index: number, count: number): number {
  return Math.min(Math.max(index, 0), count - 1);
}

export function useArticleFigureView({
  slug,
  count,
}: {
  // The figures belong to one article: reading another closes them.
  slug: string;
  count: number;
}): {
  view: FigureView;
  open: (index: number) => void;
  enlarge: () => void;
  close: () => void;
  select: (index: number) => void;
} {
  // Stamped with the article it was opened on, so reading another article
  // finds nothing open, without a reset after the first render.
  const [opened, setOpened] = useState<{ slug: string; view: FigureView }>({
    slug,
    view: { kind: "closed" },
  });
  // The figure list can shrink under an open figure: the page first renders
  // the article seeded from the feed, then the fetched detail. A figure that
  // is no longer there is no longer open.
  const view: FigureView =
    opened.slug === slug &&
    (opened.view.kind === "closed" || opened.view.index < count)
      ? opened.view
      : { kind: "closed" };
  function setView(next: FigureView): void {
    setOpened({ slug, view: next });
  }

  function open(index: number): void {
    setView({ kind: "panel", index });
  }

  function enlarge(): void {
    if (view.kind !== "panel") return;
    setView({ kind: "viewer", index: view.index });
  }

  // Esc and the close buttons: full screen steps back to the panel, the
  // panel closes.
  function close(): void {
    switch (view.kind) {
      case "closed":
        return;
      case "panel":
        setView({ kind: "closed" });
        return;
      case "viewer":
        setView({ kind: "panel", index: view.index });
        return;
      default:
        throw new Error(view satisfies never);
    }
  }

  function select(index: number): void {
    switch (view.kind) {
      case "closed":
        return;
      case "panel":
      case "viewer":
        setView({ kind: view.kind, index: clamp(index, count) });
        return;
      default:
        throw new Error(view satisfies never);
    }
  }

  // ← → step through the figures, F enters and leaves full screen, and Esc
  // closes, from anywhere on the page while a figure is open.
  useEffect(() => {
    if (view.kind === "closed") return;
    const { kind, index } = view;
    function handleKeyDown(event: KeyboardEvent): void {
      // The text beside the panel stays live: arrows and Esc typed in the
      // composer, or in any other field, belong to that field.
      if (isTyping()) return;
      // The search palette over the page takes these keys for itself.
      if (isDesktopSearchPaletteOpen()) return;
      // ⌘F and the like stay the browser's.
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // F by its place on the keyboard, so an Arabic layout has it too. Held
      // down, it toggles once.
      if (event.code === "KeyF") {
        event.preventDefault();
        if (event.repeat) return;
        if (kind === "panel") enlarge();
        else close();
        return;
      }
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          select(index - 1);
          return;
        case "ArrowRight":
          event.preventDefault();
          select(index + 1);
          return;
        case "Escape":
          event.preventDefault();
          close();
          return;
        default:
          return;
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  return { view, open, enlarge, close, select };
}
